// src/app/layout/CoreThemeToggle.tsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Palette } from 'lucide-react';
import { useTheme } from '../theme/useTheme';

const CATEGORY_LABEL: Record<string, string> = {
  system: 'سیستم',
  brand: 'برند',
  seasonal: 'فصلی',
  community: 'انجمن',
  custom: 'سفارشی',
};

/**
 * Real theme switcher. Reads `theme`, `availableThemes`, and `setTheme`
 * from the existing ThemeEngine bridge via useTheme() (the app's one
 * sanctioned theme hook) — this component owns no theme state of its
 * own beyond whether its menu is open. Mirrors NotificationArea's
 * contract (a single self-contained header icon slot); CoreHeader
 * mounts it unchanged.
 */
export function CoreThemeToggle() {
  const { theme, setTheme, availableThemes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    function updateMenuPosition() {
      const trigger = containerRef.current?.getBoundingClientRect();
      if (trigger) {
        const gutter = 12;
        const width = Math.min(224, Math.max(1, window.innerWidth - gutter * 2));
        const desiredRight = window.innerWidth - trigger.right;
        const maxRight = Math.max(gutter, window.innerWidth - width - gutter);
        const right = Math.min(maxRight, Math.max(gutter, desiredRight));
        setMenuPosition({ top: trigger.bottom + 8, right, width });
      }
    }

    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !menuPosition || !menuRef.current) return;

    const gutter = 12;
    const menuHeight = menuRef.current.getBoundingClientRect().height;
    const maxTop = Math.max(gutter, window.innerHeight - menuHeight - gutter);
    const top = Math.min(maxTop, Math.max(gutter, menuPosition.top));

    if (top !== menuPosition.top) {
      setMenuPosition((current) => current ? { ...current, top } : current);
    }
  }, [isOpen, menuPosition]);

  const activeThemeId = theme?.manifest.id ?? null;

  const orderedThemes = [...availableThemes].sort((a, b) => {
    const categoryA = a.meta?.category ?? 'custom';
    const categoryB = b.meta?.category ?? 'custom';
    if (categoryA === categoryB) return 0;
    if (categoryA === 'system') return -1;
    if (categoryB === 'system') return 1;
    return categoryA.localeCompare(categoryB);
  });

  async function handleSelect(id: string) {
    if (id === activeThemeId || pendingId) {
      setIsOpen(false);
      return;
    }
    setPendingId(id);
    setIsOpen(false);
    try {
      await setTheme(id);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        title={theme?.manifest.name ?? 'پوسته'}
        aria-label="تغییر پوسته"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--vania-text-secondary,#a6acb8)] transition-colors hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Palette size={17} strokeWidth={2.2} />
      </button>

      {isOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="انتخاب پوسته"
          className="vania-glass-card fixed z-[var(--vania-z-dropdown,100)] max-h-[calc(100vh-24px)] overflow-y-auto p-1.5"
          style={{ top: menuPosition.top, right: menuPosition.right, width: menuPosition.width }}
        >
          {orderedThemes.map((entry, index) => {
            const isActive = entry.id === activeThemeId;
            const category = entry.meta?.category ?? 'custom';
            const showCategoryLabel =
              index === 0 || (orderedThemes[index - 1]?.meta?.category ?? 'custom') !== category;

            return (
              <div key={entry.id}>
                {showCategoryLabel && (
                  <div className="px-2.5 pb-1 pt-2 text-[11px] font-medium text-[var(--vania-text-tertiary,#6c7280)] first:pt-1">
                    {CATEGORY_LABEL[category] ?? category}
                  </div>
                )}
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={isActive}
                  disabled={pendingId !== null}
                  onClick={() => handleSelect(entry.id)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-start text-sm text-[var(--vania-text-primary,#f5f6f8)] transition-colors hover:bg-[var(--vania-surface-2,rgba(255,255,255,0.06))] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="truncate">{entry.meta?.name ?? entry.id}</span>
                  {isActive && (
                    <Check size={15} strokeWidth={2.5} className="shrink-0 text-[var(--vania-accent,#5b8cff)]" />
                  )}
                </button>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
